'use client';

import { useTranslations } from 'next-intl';

import type { NavItems } from '@/types/nav';
import { useAccountInfo, useAccountStatus } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';

import { SidebarItem } from '../sidebar-item';
import { useSideBarContext } from '../../contexts/sidebar-context';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { useAuthToken } from '@/store/auth';
import { AccountActStatus, PromotionType } from '@nf/utils-common/constants';
import { LicenseeTypeEnums } from '@nf/types/site';
import { siteService } from '@/services/site.service';
import { useNavItemsComposer } from '../../hooks/use-nav-items-composer';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';
import { usePredictionMarket } from '@/hooks/use-prediction-market';

export const SidebarGeneral = () => {
	const t = useTranslations('General');
	const { user } = useAccountInfo();
	const { selectedCategory } = useSideBarContext();
	const view = useAppearanceStore(state => state.view);
	const { SiteConfigsData } = useGetSiteConfigs();
	const { canSeePredictionMarket, predictionMarketNewTagUntil } = usePredictionMarket();
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const { breakpoint } = useBreakpoint();
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;

	const composeNavItems = useNavItemsComposer();

	const gamesNavItemsGroup: NavItems = [
		{
			productName: 'casinoRoyale',
			href: '/third-party/casino-royale',
			routePrefix: '/third-party/casino-royale',
			className: 'side-features__item',
			icon: 'game-lobby',
			iconType: 'general',
			type: 'general',
			title: t('lbl_SabaSel'),
			hasStreamer: false,
			isNewWindow: false,
			isRedirectToProduct: true,
			isEnable: SiteConfigsData?.CanSeeCasinoRoyale && user?.ActStatus === AccountActStatus.Deposited
		}
	];

	const gamesFeatureNavItems: NavItems = [
	];

	const navItemsGroup: NavItems = [
		{
			href: '/lobby',
			routePrefix: '/lobby',
			className: 'side-features__item',
			icon: 'lobby',
			iconType: 'general',
			type: 'general',
			title: view === ViewEnum.galaxy ? t('lbl_Lobby') : t('lbl_home1'),
			hasStreamer: false,
			isNewWindow: false,
			isRedirectToProduct: false,
			isEnable: !isBefore && (!isAthena || breakpoint < Breakpoint.md)
		},
		{
			href: '/promotion',
			routePrefix: '/promotion',
			className: 'side-features__item',
			icon: 'giftbox',
			iconType: 'general',
			type: 'general',
			title: t('lbl_ECPPromotion'),
			needPromotion: true,
			isEnable: true,
			goldTipText: t('lbl_LotteryLocation')
		},
		{
			productName: 'streamer',
			href: '/streamer',
			routePrefix: '/streamer',
			className: 'side-features__item',
			icon: 'streamer-schedule',
			iconType: 'general',
			type: 'general',
			title: t('lbl_Streamer'),
			isEnable: SiteConfigsData?.EnableStreamerSchedule
		},
		{
			href: '/third-party/prediction-market',
			routePrefix: '/third-party/prediction-market',
			className: 'side-features__item',
			icon: 'prediction-market',
			iconType: 'general',
			type: 'general',
			title: t('lbl_PredictMarket'),
			isNewWindow: true,
			isEnable: canSeePredictionMarket,
			newTagUntil: predictionMarketNewTagUntil
		},
		{
			href: '/favorites',
			className: 'side-features__item',
			icon: view === ViewEnum.mars ? 'favorite' : 'favorite-outline',
			iconType: 'general',
			title: t('lbl_favorite'),
			type: 'general',
			needAuth: true,
			hasStreamer: false,
			isNewWindow: false,
			isRedirectToProduct: false,
			isEnable: view === ViewEnum.mars && user?.ActStatus === AccountActStatus.Deposited && SiteConfigsData?.EnableMyFavorite
		},
		{
			href: '/bets/u',
			routePrefix: '/bets',
			className: 'side-features__item',
			icon: 'my-bet',
			iconType: 'general',
			type: 'general',
			title: view === ViewEnum.galaxy ? t('lbl_MyBets') : t('lbl_Statement'),
			needAuth: true,
			useTip: true,
			hasStreamer: false,
			isNewWindow: false,
			isRedirectToProduct: false,
			isEnable: user?.ActStatus === AccountActStatus.Deposited
		},
	];

	const featureNavItems: NavItems = [
		{
			href: '/search',
			className: 'side-features__item',
			icon: 'search',
			iconType: 'general',
			type: 'general',
			title: t('lbl_search'),
			hasStreamer: false,
			isNewWindow: false,
			isRedirectToProduct: false,
			isEnable: !SiteConfigsData?.EnableFilterEvent && view === ViewEnum.galaxy ? true : false
		},
		{
			href: '/favorites',
			className: 'side-features__item',
			icon: view === ViewEnum.mars ? 'favorite' : 'favorite-outline',
			iconType: 'general',
			title: t('lbl_favorite'),
			type: 'general',
			needAuth: true,
			hasStreamer: false,
			isNewWindow: false,
			isRedirectToProduct: false,
			isEnable: view === ViewEnum.galaxy && user?.ActStatus === AccountActStatus.Deposited && SiteConfigsData?.EnableMyFavorite
		}
	];

	const { data: actStatus } = useAccountStatus();
	const navItemsGroupList = selectedCategory === 'Sports' ? navItemsGroup : gamesNavItemsGroup;
	let navItemsGroupToDisplay = navItemsGroupList.filter(item => item.isEnable);
	if (actStatus !== 2) {
		navItemsGroupToDisplay = navItemsGroupToDisplay.filter(item => !item.needAuth)
	}
	if (!SiteConfigsData?.EnableLicLobby) {
		navItemsGroupToDisplay = navItemsGroupToDisplay.filter(item => item.icon !== 'lobby')
	}

	if (selectedCategory === 'Sports' && !(SiteConfigsData?.EnablePromotion ?? 0 > PromotionType.Close)) {
		navItemsGroupToDisplay = [...navItemsGroupToDisplay].filter(item => !item.needPromotion);
	}

	navItemsGroupToDisplay = composeNavItems(navItemsGroupToDisplay);
	
	const featureNavItemsList = selectedCategory === 'Sports' ? featureNavItems : gamesFeatureNavItems;
	let navItemsToDisplay = actStatus === 2
		? featureNavItemsList.filter(item => item.isEnable)
		: featureNavItemsList.filter(item => !item.needAuth && item.isEnable);
	navItemsToDisplay = composeNavItems(navItemsToDisplay);

	return (
		<>
			{navItemsGroupToDisplay.length > 0 ? (
				<div className="side-features">
					{navItemsGroupToDisplay.map(item => (
						<SidebarItem
							key={item.href}
							item={item}
						>
							{item.title}
						</SidebarItem>
					))}
				</div>
			) : null}

			{navItemsToDisplay.length > 0 ? (
				<div className="side-features">
					{navItemsToDisplay.map(item => (
						<SidebarItem
							key={item.href}
							item={item}
						>
							{item.title}
						</SidebarItem>
					))}
				</div>
			) : null}
		</>
	);
};